import React, { useState } from "react";
import { Mail as MailIcon, Phone as PhoneIcon, MessageCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LegalLayout, BRAND } from "@/components/Legal";

const EMPTY = { name: "", email: "", phone: "", message: "" };
const inp = "bg-[#1A0F0B] border-[#3B251D] text-[#FDFBF7]";

const Card = ({ icon: Icon, label, value, href, testid }) => (
  <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={testid}
    className="lux-card p-6 hover-lift flex items-start gap-4">
    <div className="h-11 w-11 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] flex items-center justify-center shrink-0"><Icon size={20} /></div>
    <div><p className="text-[10px] uppercase tracking-widest text-[#8C7B6E]">{label}</p><p className="text-[#FDFBF7] mt-1 break-all">{value}</p></div>
  </a>
);

export default function Contact() {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name || !f.email || !f.phone || !f.message) return toast.error("Please fill in all fields");
    setBusy(true);
    try {
      await api.post("/gift-requests", { type: "contact", ...f });
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setF(EMPTY);
    } catch (e) {
      toast.error("Please check your email address and try again.");
    } finally { setBusy(false); }
  };
  return (
    <LegalLayout title="Contact Us" eyebrow="We'd love to hear from you" testid="contact-page">
      <div className="grid sm:grid-cols-3 gap-4 not-prose">
        <Card icon={MailIcon} label="Email" value={BRAND.email} href={`mailto:${BRAND.email}`} testid="contact-email" />
        <Card icon={PhoneIcon} label="Phone" value={BRAND.phone} href="tel:+917622927835" testid="contact-phone" />
        <Card icon={MessageCircle} label="WhatsApp" value={BRAND.phone} href={BRAND.wa} testid="contact-whatsapp" />
      </div>
      <p>For orders, cancellations, delivery questions, corporate gifting and customisation, reach us on any channel above. We aim to respond within 24 hours. Please keep your Order ID handy for order-related queries.</p>
      <div className="lux-card p-6 sm:p-8">
        <h2 className="font-display text-2xl text-[#FDFBF7] mb-5">Send us a message</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input data-testid="contact-name" placeholder="Full name *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} />
          <Input data-testid="contact-phone-input" placeholder="Phone *" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inp} />
          <Input data-testid="contact-email-input" placeholder="Email *" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={`${inp} sm:col-span-2`} />
          <Textarea data-testid="contact-message" placeholder="How can we help? *" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={`${inp} sm:col-span-2 min-h-[120px]`} />
        </div>
        <Button data-testid="contact-submit-btn" onClick={submit} disabled={busy} className="btn-gold rounded-full mt-5 px-8 h-12">
          {busy ? <Loader2 className="animate-spin" size={18} /> : "Send Message"}
        </Button>
      </div>
      <p className="text-xs text-[#8C7B6E]">Grievance Officer: reachable at {BRAND.email} / {BRAND.phone}. Complaints are acknowledged within 48 hours.</p>
    </LegalLayout>
  );
}
